/**
 * UI Preferences Store — layout modes and display preferences.
 *
 * Controls the strip/dock mode toggle for the visit detail panel.
 * - strip: Minimal overlay — just the SOAP note (compact)
 * - dock: Full panel — transcript, SOAP, alerts, run console
 *
 * @module stores/ui-store
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type PanelMode = 'strip' | 'dock';

export interface UiState {
  /** Panel display mode */
  panelMode: PanelMode;
  /** Whether the run console is visible in dock mode */
  showRunConsole: boolean;
  /** Whether the alerts panel is visible in dock mode */
  showAlerts: boolean;
}

interface UiActions {
  setPanelMode: (mode: PanelMode) => void;
  togglePanelMode: () => void;
  setShowRunConsole: (show: boolean) => void;
  setShowAlerts: (show: boolean) => void;
}

type UiStore = UiState & UiActions;

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

const initialState: UiState = {
  panelMode: 'dock',
  showRunConsole: true,
  showAlerts: true,
};

export const useUiStore = create<UiStore>()(
  immer((set) => ({
    ...initialState,

    setPanelMode: (mode) => {
      set((state) => {
        state.panelMode = mode;
      });
    },

    togglePanelMode: () => {
      set((state) => {
        state.panelMode = state.panelMode === 'strip' ? 'dock' : 'strip';
      });
    },

    setShowRunConsole: (show) => {
      set((state) => {
        state.showRunConsole = show;
      });
    },

    setShowAlerts: (show) => {
      set((state) => {
        state.showAlerts = show;
      });
    },
  })),
);
